import React from "react";
import "../../css/Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Headset } from "lucide-react";

const Row = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={[
      "w-full",
      "text-left",
      "text-sm",
      "font-semibold",
      "rounded-xl",
      "transition-colors",
      "px-4 py-3",
      "flex items-center gap-3", // FLEX: icon + label
      "hover:bg-[#616161]",
      active ? "bg-[#616161]" : "",
    ].join(" ")}
  >
    {children}
  </button>
);

const ImgIcon = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="h-6 w-6 shrink-0 opacity-100 pointer-events-none"
  />
);

const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0  bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={`sidebar h-screen w-56 text-white transition-all duration-300 fixed lg:relative z-50 overflow-y-auto py-4 ${isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        style={{
          backgroundColor: "#1F1E1E",
          borderRadius: "24px",
          zIndex: 9998,
          top: 0,
          left: 0,
          height: "100vh"
        }}
      >
        <div className="flex items-center justify-center h-16 px-4 mb-4">
          {/* Logo */}
          <img
            src="/assets/Transparent background.png"
            alt="Ducktail Logo"
            onClick={() => handleNavigation("/")}
            className="h-12 pr-4 cursor-pointer"
          />
        </div>

        {/* Navigation links */}
        <nav className="mt-2 space-y-2">
          {/* 1. Project Invite */}
          <Row
            active={isActive("/customer/projectinvite")}
            onClick={() => handleNavigation("/customer/projectinvite")}
          >
            <ImgIcon src="/sidebaricons/project invite 1.png" alt="Project Invite" />
            <span>Project Invite</span>
          </Row>

          {/* 2. Live Project */}
          <Row
            active={isActive("/customer/livecard")}
            onClick={() => handleNavigation("/customer/livecard")}
          >
            <ImgIcon src="/sidebaricons/Job list3.png" alt="Live Project" />
            <span>Live Project</span>
          </Row>

          {/* 3. Hold Project */}
          <Row
            active={isActive("/customer/holdcard")}
            onClick={() => handleNavigation("/customer/holdcard")}
          >
            <ImgIcon src="/sidebaricons/live1.png" alt="Hold Project" />
            <span>Hold Project</span>
          </Row>

          {/* 4. Rejection Projects */}
          <Row
            active={isActive("/customer/rejectcard")}
            onClick={() => handleNavigation("/customer/rejectcard")}
          >
            <ImgIcon src="/sidebaricons/rejected1.png" alt="Rejection Projects" />
            <span>Rejection Projects</span>
          </Row>

          {/* 5. Completion Request */}
          <Row
            active={isActive("/customer/requestcard")}
            onClick={() => handleNavigation("/customer/requestcard")}
          >
            <ImgIcon src="/sidebaricons/faq_1761715511.png" alt="Completion Request" />
            <span>Completion Request</span>
          </Row>

          {/* 6. Completed Projects */}
          <Row
            active={isActive("/customer/completedcard")}
            onClick={() => handleNavigation("/customer/completedcard")}
          >
            <ImgIcon src="/sidebaricons/Profile 2.png" alt="Completed Projects" />
            <span>Completed Projects</span>
          </Row>

          {/* 7. Profile */}
          <Row
            active={isActive("/customer/profile")}
            onClick={() => handleNavigation("/customer/profile")}
          >
            <ImgIcon src="/sidebaricons/Profile1.png" alt="Profile" />
            <span>Profile</span>
          </Row>

          {/* 8. Notification */}
          <Row
            active={isActive("/customer/notify")}
            onClick={() => handleNavigation("/customer/notify")}
          >
            {/* Using a placeholder or Lucide icon if ImgIcon expects src. 
                Since ImgIcon insists on an img tag, checking if I need to import an icon or if I can use a generic src.
                However, to use Lucide icons, I should modify Row usage or assume ImgIcon is flexible?
                The Row component renders {children}. I can pass <Bell /> instead of <ImgIcon />.
                I need to import Bell and Headset from 'lucide-react'.
            */}
            {/* Let's verify imports in the file first? No imports of Lucide in Sidebar.jsx. 
                I will add imports in a separate block or use existing if any? No existing.
                I will use a separate replace for imports. For now, I'll place the code assuming imports exist.
            */}
            <Bell className="w-6 h-6 text-current" />
            <span>Notification</span>
          </Row>

          {/* 9. Support */}
          <Row
            active={isActive("/customer/help")}
            onClick={() => handleNavigation("/customer/help")}
          >
            <Headset className="w-6 h-6 text-current" />
            <span>Support</span>
          </Row>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
